// The seven AI-readiness signals, each paired with the Idukki fix that closes
// it. Single source of truth shared by the static "seven signals, one score"
// scorecard (app/(marketing)/ai-readiness/page.tsx) and the live scan results
// (components/tools/ai-readiness-scan.tsx), so a failing ("red") row and its
// educational row name the SAME remediation. Keyed by id, which matches the
// scanner's API contract check ids.
//
// Content integrity: the fixes describe capabilities Idukki already ships
// (agentfeed llms.txt/MCP, Product + Review JSON-LD, QnA) or guidance the scan
// returns. No invented stats, customers, or claims.

export type AiSignalId =
  | 'crawlers'
  | 'llmsTxt'
  | 'structuredData'
  | 'productFeed'
  | 'sitemap'
  | 'metadata'
  | 'faq'

export interface AiSignal {
  id: AiSignalId
  /** Short signal name. */
  label: string
  /** What the scan grades for this signal. */
  body: string
  /** The Idukki capability (or guidance) that closes the gap. */
  fixLabel: string
  fixBody: string
  /** Deep link for the "Fix with Idukki" CTA. */
  fixHref: string
}

const REGISTER = (id: AiSignalId) => `https://app.idukki.io/register?source=ai-readiness-${id}`

export const AI_SIGNALS: AiSignal[] = [
  {
    id: 'crawlers',
    label: 'AI crawler access',
    body: 'Does robots.txt let GPTBot, ClaudeBot, PerplexityBot, Google-Extended and CCBot in? Block one and that engine cannot read you, so it cannot cite you.',
    fixLabel: 'Crawler guidance',
    fixBody: 'We name the exact bots your robots.txt blocks and the one-line allow you need, so every engine can read you.',
    fixHref: REGISTER('crawlers'),
  },
  {
    id: 'llmsTxt',
    label: 'llms.txt at the root',
    body: 'A short table of contents that points AI agents at your canonical, answer-ready pages. The cheapest single fix, and most stores do not have one.',
    fixLabel: 'agentfeed publishes it',
    fixBody: 'Idukki maintains an llms.txt for your catalogue, so AI agents land on your canonical pages instead of guessing.',
    fixHref: REGISTER('llmsTxt'),
  },
  {
    id: 'structuredData',
    label: 'Structured data (JSON-LD)',
    body: 'Product, Organization and Review schema let an engine read what you sell without scraping prose. Structured data gets cited far more than plain copy.',
    fixLabel: 'JSON-LD on UGC + products',
    fixBody: 'Every gallery and tagged product ships Product and Review JSON-LD, the structure engines cite over scraped prose.',
    fixHref: REGISTER('structuredData'),
  },
  {
    id: 'productFeed',
    label: 'Agent-readable product feed',
    body: 'A machine-readable feed of your catalogue (price, availability, image) is what an agent quotes when a shopper asks it to compare or buy.',
    fixLabel: 'agentfeed JSON + MCP',
    fixBody: 'A clean product + UGC feed in JSON-LD and over MCP, the format agentic shopping surfaces read directly.',
    fixHref: REGISTER('productFeed'),
  },
  {
    id: 'sitemap',
    label: 'Sitemap + URL structure',
    body: 'A clean sitemap and canonical URLs tell a crawler what exists and which version to trust, so variants do not compete for the same citation.',
    fixLabel: 'Canonical-clean surfaces',
    fixBody: 'The scan flags the duplicate and variant URLs splitting your citations and the canonical each should point at.',
    fixHref: REGISTER('sitemap'),
  },
  {
    id: 'metadata',
    label: 'Quotable metadata',
    body: 'A fact-shaped title and meta description, 120-200 characters, that an engine can lift verbatim into its answer snippet.',
    fixLabel: 'Quotable metadata',
    fixBody: 'We show the fact-shaped title and description an engine lifts into its answer, per template, not keyword-stuffed copy.',
    fixHref: REGISTER('metadata'),
  },
  {
    id: 'faq',
    label: 'FAQ / Q&A markup',
    body: 'Questions and answers marked up so an engine can match a shopper question to your answer and quote it back with you as the source.',
    fixLabel: 'QnA on your PDPs',
    fixBody: 'Idukki QnA puts marked-up questions and answers on your PDPs, the exact thing an engine matches a query against.',
    fixHref: REGISTER('faq'),
  },
]

/** Fast id → signal lookup for the live scan rows. */
export const AI_SIGNAL_BY_ID: Record<AiSignalId, AiSignal> = AI_SIGNALS.reduce(
  (acc, s) => {
    acc[s.id] = s
    return acc
  },
  {} as Record<AiSignalId, AiSignal>,
)
